import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded'
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { displayNamesOfEventTypes } from 'src/consumer/constants'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import {
  reassignSupportBookedEvent,
  getSupportReassignOptions,
} from 'src/supportPortal/services/supportBookedEventActionsService'

dayjs.extend(utc)

function getPatientName(bookedEvent) {
  return bookedEvent?.patientInfoData?.name || bookedEvent?.userName || 'Patient'
}

export default function SupportBookedEventReassignDrawer({ open, onClose, bookedEvent, onSuccess }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [searchInput, setSearchInput] = useState('')
  const [options, setOptions] = useState([])
  const [searching, setSearching] = useState(false)
  const [optionsError, setOptionsError] = useState('')
  const [appointmentType, setAppointmentType] = useState(null)
  const [selectedPractitioner, setSelectedPractitioner] = useState(null)
  const [notifyPatient, setNotifyPatient] = useState(false)
  const [notifyPractitioners, setNotifyPractitioners] = useState(true)
  const [reason, setReason] = useState('Reassigned by support')
  const [saving, setSaving] = useState(false)
  const [submitError, setSubmitError] = useState('')

  useEffect(() => {
    if (!open) {
      return
    }
    setSearchInput('')
    setOptions([])
    setOptionsError('')
    setAppointmentType(null)
    setSelectedPractitioner(null)
    setNotifyPatient(false)
    setNotifyPractitioners(true)
    setReason('Reassigned by support')
    setSubmitError('')
  }, [open, bookedEvent])

  useEffect(() => {
    if (!open || !bookedEvent?.id) return undefined
    let active = true
    const loadOptions = async () => {
      setSearching(true)
      setOptionsError('')
      const result = await getSupportReassignOptions({
        clinicBrandId: bookedEvent?.clinicBrandId,
        clinicId: bookedEvent?.clinicId,
        eventType: bookedEvent?.eventType,
        eventTypeId: bookedEvent?.eventTypeId,
        useClinicServices: bookedEvent?.useClinicServices,
      })
      if (!active) return
      setOptions((result.practitioners || []).filter((item) => item?.id && item.id !== bookedEvent?.mentorId))
      setAppointmentType(result.appointmentType || null)
      setOptionsError(result.message || '')
      setSearching(false)
    }
    loadOptions()

    return () => {
      active = false
    }
  }, [
    bookedEvent?.id,
    bookedEvent?.mentorId,
    bookedEvent?.clinicBrandId,
    bookedEvent?.clinicId,
    bookedEvent?.eventType,
    bookedEvent?.eventTypeId,
    bookedEvent?.useClinicServices,
    open,
  ])

  const eventTypeLabel = useMemo(
    () => displayNamesOfEventTypes?.[bookedEvent?.eventType] || bookedEvent?.eventType || 'Session',
    [bookedEvent?.eventType],
  )

  async function handleSubmit() {
    if (!selectedPractitioner?.id) {
      toast.error('Please choose the new practitioner')
      return
    }
    setSaving(true)
    setSubmitError('')
    const result = await reassignSupportBookedEvent({
      bookedEventId: bookedEvent?.id,
      newMentorId: selectedPractitioner.id,
      sendCommunicationToClient: notifyPatient,
      sendCommunicationToMentor: notifyPractitioners,
      reason: reason?.trim() || undefined,
    })
    setSaving(false)

    if (result.ok) {
      toast.success(`Booking reassigned to ${selectedPractitioner.name || 'the new practitioner'}`)
      onSuccess?.()
      onClose?.()
      return
    }
    setSubmitError(result.message)
    toast.error(result.message)
  }

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 460,
          maxWidth: '100%',
          height: isMobile ? '88vh' : '100vh',
          borderTopLeftRadius: isMobile ? 20 : 0,
          borderTopRightRadius: isMobile ? 20 : 0,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
            background: 'linear-gradient(180deg, rgba(79, 70, 229, 0.06) 0%, rgba(255, 255, 255, 0.98) 100%)',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <SwapHorizRoundedIcon sx={{ color: '#4f46e5', fontSize: 22 }} />
                <Typography sx={{ fontSize: 19, fontWeight: 800, color: '#0f172a' }}>
                  Reassign Practitioner
                </Typography>
              </Stack>
              <Typography sx={{ color: '#475569', fontSize: 13 }}>
                {getPatientName(bookedEvent)} · {eventTypeLabel}
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: 12, mt: 0.4 }}>
                Currently with {bookedEvent?.mentorName || 'unassigned'} ·{' '}
                {bookedEvent?.startTime
                  ? dayjs.utc(bookedEvent.startTime).local().format('DD MMM, h:mm A')
                  : 'time not set'}
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5 }}>
          <Stack spacing={2}>
            {submitError ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {submitError}
              </Alert>
            ) : null}

            {optionsError ? <Alert severity="error" sx={{ borderRadius: 2 }}>{optionsError}</Alert> : null}

            {appointmentType ? (
              <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3, borderColor: 'rgba(14, 116, 144, 0.25)' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#0e7490', letterSpacing: 0.5 }}>
                  CLINIC APPOINTMENT
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a', mt: 0.4 }}>
                  {appointmentType.title || eventTypeLabel}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.35 }}>
                  This appointment type and its configured plan will remain attached to the booking.
                </Typography>
              </Paper>
            ) : null}

            <Autocomplete
              options={options}
              value={selectedPractitioner}
              onChange={(event, nextValue) => setSelectedPractitioner(nextValue)}
              inputValue={searchInput}
              onInputChange={(event, nextValue) => setSearchInput(nextValue)}
              getOptionLabel={(option) => option?.name || option?.slug || option?.id || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              loading={searching}
              noOptionsText={searching
                ? 'Checking clinic eligibility…'
                : 'No eligible practitioners at this clinic location'}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                    <Avatar
                      src={option.profilePicId ? getImageLink(option.profilePicId) : undefined}
                      sx={{ width: 30, height: 30, fontSize: 13 }}
                    >
                      {(option.name || '?').charAt(0)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }} noWrap>
                        {option.name}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: '#64748b' }} noWrap>
                        {option.email || option.slug}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="New practitioner"
                  size="small"
                  placeholder="Search eligible practitioners"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searching ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 3, borderColor: 'rgba(148, 163, 184, 0.28)' }}
            >
              <Stack spacing={0.5}>
                <TextField
                  label="Reassignment note"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  sx={{ mb: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={notifyPatient}
                      onChange={(event) => setNotifyPatient(event.target.checked)}
                    />
                  }
                  label="Notify patient about the change"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={notifyPractitioners}
                      onChange={(event) => setNotifyPractitioners(event.target.checked)}
                    />
                  }
                  label="Notify practitioner(s) about the change"
                />
              </Stack>
            </Paper>

            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Only clinic-affiliated practitioners with an approved service and access to this clinic
              appointment type are shown. The server checks their calendar again before saving.
            </Alert>
          </Stack>
        </Box>

        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderTop: '1px solid rgba(148, 163, 184, 0.2)',
            backgroundColor: '#ffffff',
          }}
        >
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button onClick={onClose} color="inherit">
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!selectedPractitioner || saving}
              sx={{ minWidth: 170 }}
            >
              {saving ? 'Reassigning...' : 'Reassign Booking'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  )
}
